import classNames from 'classnames';
import { ErrorMessage, Field, Formik } from 'formik';
import { FocusEvent, useCallback, useContext, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import * as yup from 'yup';
import Button from '../../../components/Button/Button';
import { UserAlreadyExistsError, checkEmailExists, login, signup } from '../../../lib/authentication';
import { saneEmail } from '../../../lib/utils';
import RegisterChipContextV2 from '../RegisterChipContextV2';
import styles from './EmailStepV2.module.scss';
import StepProgress from '../StepProgress/StepProgress';
import { useScrollToStepTop } from '../useScrollToStepTop';
import getCustomerIO from '../../../lib/customerIO';
import {
  trackNanoEmailSwitchedToLogin,
  trackNanoStepFailed,
  trackNanoStepSubmitted,
} from '../../../lib/analytics/events';
import SingleTextFieldFormGroup from '@/components/form/SingleTextFieldFormGroup';

interface LoginFormProps {
  hasAccount: boolean;
  onSuccess: (email: string) => Promise<void>;
  /** An account already owns the email, so switch this form to login and keep the address. */
  onAccountExists: (email: string) => void;
}

interface FormValues {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

function LoginForm({ hasAccount, onSuccess, onAccountExists }: LoginFormProps) {
  const history = useHistory();
  const { registerState } = useContext(RegisterChipContextV2);
  // Explains the switch to login without using the error styling, since nothing has gone wrong.
  const [notice, setNotice] = useState<string | undefined>();
  // Emails the lookup has already resolved, so re-blurring the field does not re-request.
  const checkedEmails = useRef<Map<string, boolean>>(new Map());
  const initialFormValues: FormValues = {
    email: registerState.primaryEmail || '',
    password: '',
    firstName: '',
    lastName: '',
  };

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(`Please enter a valid email address`)
      .test('sane_email', `.con is not valid. Please check the spelling of your email address`, saneEmail)
      .required(`Please enter your email address`),
    ...(hasAccount
      ? {}
      : {
          firstName: yup.string().trim().required(`Please enter your first name`),
          lastName: yup.string().trim().required('Please enter your last name'),
        }),
  });

  // Moves the form to login and keeps the address. Called from the lookup on blur, and from the
  // signup 409 when the lookup was skipped or unavailable.
  const switchToLogin = useCallback(
    (email: string, source: 'lookup' | 'signup_rejected', setFieldValue: (field: string, value: string) => void) => {
      trackNanoEmailSwitchedToLogin(source);
      onAccountExists(email);
      // Clear the password: the signup field is hidden from 1Password and marked `new-password`, so
      // anything in it is an invented password rather than their Fi one, and submitting it would
      // fail login. Empty lets the manager offer the saved credential on the login field instead.
      setFieldValue('password', '');
      setNotice('That email already has a Fi account. Enter your password to log in.');
    },
    [onAccountExists],
  );

  // Resolves the email before signup is attempted, the way ecommerce's stripe checkout does, so an
  // existing customer is moved to login instead of being rejected first.
  const lookUpEmail = useCallback(
    async (rawEmail: string, setFieldValue: (field: string, value: string) => void) => {
      const email = rawEmail.trim().toLowerCase();
      if (hasAccount || !email || !yup.string().email().isValidSync(email)) {
        return;
      }

      const cached = checkedEmails.current.get(email);
      if (cached !== undefined) {
        if (cached) {
          switchToLogin(email, 'lookup', setFieldValue);
        }
        return;
      }

      try {
        const exists = await checkEmailExists(email);
        checkedEmails.current.set(email, exists);
        if (exists) {
          switchToLogin(email, 'lookup', setFieldValue);
        }
      } catch {
        // The signup 409 still catches existing accounts, so a failed lookup needs no user-facing
        // handling -- it just costs them the one error this is meant to avoid.
      }
    },
    [hasAccount, switchToLogin],
  );

  return (
    <div>
      <Formik<FormValues>
        onSubmit={async (values, { setSubmitting, setStatus, setFieldValue }) => {
          // `mode` matters here: logging in to an existing account and creating a new one fail for
          // completely different reasons, and this step is the second-largest drop-off in the funnel.
          trackNanoStepSubmitted('email', { mode: hasAccount ? 'login' : 'signup' });
          setNotice(undefined);
          try {
            if (hasAccount) {
              await login(values.email, values.password);
            } else {
              const firstName = values.firstName.trim();
              const lastName = values.lastName.trim();
              await signup(values.email, values.password, firstName, lastName);
            }

            await onSuccess(values.email);
          } catch (err) {
            // Fallback for when the blur lookup was skipped or failed: signup is the default branch
            // for everyone, since `hasAccount` is seeded from a query param that is almost never
            // present, so an existing customer would otherwise be left on a dead end.
            if (err instanceof UserAlreadyExistsError) {
              trackNanoStepFailed('email', 'already_registered', { mode: 'signup' });
              switchToLogin(values.email, 'signup_rejected', setFieldValue);
              return;
            }
            trackNanoStepFailed('email', 'account_error', { mode: hasAccount ? 'login' : 'signup' });
            setStatus({ errorMessage: (err as any).message });
          } finally {
            setSubmitting(false);
          }
        }}
        validationSchema={validationSchema}
        initialValues={initialFormValues}
      >
        {({ handleSubmit, handleBlur, isSubmitting, status, submitForm, setFieldValue }) => (
          <form onSubmit={handleSubmit} className="form">
            <div className="form-section">
              {notice && <div className={styles.notice}>{notice}</div>}
              {!hasAccount && (
                <>
                  <div className="form-group">
                    <div className="form-field">
                      <Field
                        type="text"
                        name="firstName"
                        placeholder="First name"
                        aria-label="First name"
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="form-field">
                      <Field
                        type="text"
                        name="lastName"
                        placeholder="Last name"
                        aria-label="Last name"
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                  <ErrorMessage name="firstName" component="div" className="form-field__error" />
                  <ErrorMessage name="lastName" component="div" className="form-field__error" />
                </>
              )}
              <div className="form-group">
                <div className="form-field">
                  <Field
                    type="text"
                    name="email"
                    placeholder="Email address"
                    autoComplete="email"
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                      handleBlur(e);
                      void lookUpEmail(e.target.value, setFieldValue);
                    }}
                  />
                  <ErrorMessage name="email" component="div" className="form-field__error" />
                </div>
              </div>
              <div className="form-group">
                <div className="form-field">
                  <SingleTextFieldFormGroup
                    name="password"
                    placeholder="Password"
                    type="password"
                    autoComplete={hasAccount ? 'current-password' : 'new-password'}
                    ignore1Password={!hasAccount}
                  />
                  <ErrorMessage name="password" component="div" className="form-field__error" />
                </div>
              </div>
              <div className="form-group form-group--action form-group--action__wide">
                <div className="form-field">
                  <Button
                    primary
                    type="submit"
                    label={hasAccount ? 'Log in' : 'Sign up'}
                    disabled={isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      submitForm();
                    }}
                  />
                  <Button
                    primary
                    type="button"
                    label="Go back"
                    onClick={(e) => {
                      e.preventDefault();
                      history.replace('/register/chip');
                    }}
                  />
                </div>
                {status && status.errorMessage && <div className="form-field__error">{status.errorMessage}</div>}
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default function EmailStep() {
  const { registerChipAddEmail, registerState, onStepFinished, setPrimaryEmail } = useContext(RegisterChipContextV2);
  const [hasAccount, setHasAccount] = useState(registerState.primaryEmail ? true : false);
  useScrollToStepTop();

  const onEmailSubmit = useCallback(
    async (email: string) => {
      if (!registerState.chipId) {
        throw new Error('Chip ID not found');
      }

      // Register the chip to this email address
      await registerChipAddEmail(registerState.chipId);
      setPrimaryEmail(email);
      getCustomerIO()?.identify(email, { nano: true });
      getCustomerIO()?.track('Nano Registration: Signup/Login Step Completed', { email });
      onStepFinished('email');
    },
    [onStepFinished, registerChipAddEmail, registerState.chipId, setPrimaryEmail],
  );

  let title = 'Create your Fi account';
  let subtitle =
    'The chip is registered to this account, so you can update your details any time. If your pet is found, this is the email we contact.';
  if (registerState.chipDetails?.isAssigned && !registerState.isShelterluvUser) {
    title = 'Request transfer';
    subtitle =
      'Looks like someone already registered this chip. To transfer ownership, start by creating an account or logging in.';
  } else if (hasAccount) {
    title = 'Log in to your account';
  }

  return (
    <div className={classNames('email-step', styles.container)}>
      <div className={styles.mainContent}>
        <StepProgress step={2} />
        <div className={styles.titleContainer}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subtitle}>{subtitle}</div>
        </div>
        <LoginForm
          onSuccess={onEmailSubmit}
          hasAccount={hasAccount}
          onAccountExists={(email) => {
            // Store the email as well as flipping the form: `hasAccount` is derived from
            // `registerState.primaryEmail`, so stepping back and forward lands on login rather than
            // repeating the same dead end.
            setPrimaryEmail(email);
            setHasAccount(true);
          }}
        />
        <div className={styles.accountPrompts}>
          {!hasAccount && (
            <>
              <div className={styles.accountPrompt}>
                Already have a Fi account? <button onClick={() => setHasAccount(true)}>Log in</button>
              </div>
            </>
          )}
          {hasAccount && (
            <>
              <Link to="/passwordReset" className={styles.link}>
                Forgot your password?
              </Link>
              <div className={styles.accountPrompt}>
                Don't have an account? <button onClick={() => setHasAccount(false)}>Sign up</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
